/**
 * The quantifiers a `collection` leaf may carry, as react-querybuilder's own option shape.
 *
 * Every collection is offered the three that ask whether any, none or all of its rows hold. The
 * three count modes (`atLeast` / `atMost` / `exactly`) are offered only on a collection whose rows
 * are records (`COLLECTION_ROWS_RECORDS` — one per party on `claim.involved_parties`), where "at
 * least 2 of them" counts what the author pictures. A collection whose provider groups its rows
 * into distinct tuples before publishing (`claim.computed.communications`) would count tuples, so
 * the compiler refuses a count there with a dedicated 400 — and a mode offered here is a mode the
 * author can pick and then fail to save. Handing RQB an explicit option array rather than
 * `matchModes: true` is what restricts the offer: `getMatchModesUtil` returns all six for `true`
 * and exactly the list for an array.
 *
 * The labels are the offer, not decoration: the row reads `Communications / at least one of them
 * / Direction is Outgoing`, so the mode cell is a phrase and not a wire token. A count mode's
 * phrase leaves a gap for the number the cell asks for beside it.
 *
 * `some` is first because react-querybuilder seeds a freshly-picked collection field with
 * `getFirstOption(...)` of this list, and "at least one of them" is the quantifier an author
 * reaching for a collection almost always wants.
 *
 * Kept in its own module rather than beside the builder that reads it because
 * `cms_tests/modules/rules_engine/test_be_fe_operator_contract.py` parses this file to assert
 * that both phrase maps and `rules_engine/cel/compiler.py`'s `_MATCH_MODE_MACROS` and
 * `_MATCH_COUNT_MODES` agree in both directions, and that BE-side parse raises rather than
 * skipping when it cannot find a declaration.
 */

import type { CollectionBlockCarrier } from './types';
import { COLLECTION_ROWS_RECORDS, collectionRows } from './types';

export const MATCH_MODE_PHRASES: Record<string, string> = {
  some: 'at least one of them',
  none: 'none of them',
  all: 'all of them',
};

export const COUNT_MATCH_MODE_PHRASES: Record<string, string> = {
  atLeast: 'at least … of them',
  atMost: 'at most … of them',
  exactly: 'exactly … of them',
};

export interface MatchModeOption {
  name: string;
  value: string;
  label: string;
}

/** A phrase map in the shape react-querybuilder's `Field#matchModes` takes. `name` and `value` are
 *  both required by `FlexibleOption`, and RQB reads the identifier off `value`. Declaration order
 *  is preserved, which is what makes `some` the seeded default. */
const matchModeOptions = (phrases: Record<string, string>): MatchModeOption[] =>
  Object.entries(phrases).map(([mode, label]) => ({ name: mode, value: mode, label }));

/** `MATCH_MODE_PHRASES` as options, offered on every collection. Derived from the map rather than
 *  restated, so the two cannot disagree about which modes are offered. */
const OFFERED_MATCH_MODES: MatchModeOption[] = matchModeOptions(MATCH_MODE_PHRASES);

const COUNT_MATCH_MODES: MatchModeOption[] = matchModeOptions(COUNT_MATCH_MODE_PHRASES);

const OFFERED_ON_RECORDS: MatchModeOption[] = [...OFFERED_MATCH_MODES, ...COUNT_MATCH_MODES];

/** Whether `mode` is one of the count modes, which compare the number of matching rows against
 *  `match.threshold`. */
export const isCountMatchMode = (mode: unknown): boolean =>
  typeof mode === 'string' && COUNT_MATCH_MODES.some((option) => option.value === mode);

/** What a collection field's mode cell offers: the three quantifiers, then the count modes where
 *  its rows are records — the condition the compiler checks before it accepts a count. The same
 *  for a collection nested as an element of another, which declares its rows in its own block. */
export const matchModesFor = (field: CollectionBlockCarrier): MatchModeOption[] =>
  collectionRows(field) === COLLECTION_ROWS_RECORDS ? OFFERED_ON_RECORDS : OFFERED_MATCH_MODES;
